import { OptionsMenuWrapper } from 'src/components/stowage/tankPlanView/TankPlanViewer.styles';
import OdfjellContextMenu, { MenuItem } from 'src/components/ui/odfjellContextMenu/OdfjellContextMenu';
import { MenuItemButton } from '../../PageHeader.styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { stowageFeatureEnum } from 'src/enums/stowage/stowageFeatureEnum';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import { hasPermission } from 'src/utils/stowage/permissions/stowagePermissionsUtils';
import usePostStowageApi from 'src/api/stowage/usePostStowageApi';
import { useAuthStore } from 'src/context/AuthContext';
import { useStowageStore } from 'src/context/StowageContext';
import { sortStowagePlans, getMasterStowagePlan } from 'src/utils/stowage/stowagePlanUtils';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { ContextMenuState } from 'src/types/ViewModel/Vessel/TankPlan';

const PageHeaderContextMenu = () => {
    const { updateStowagePlan } = usePostStowageApi();
    const {
        setIsStowagePlanListMenuOpen,
        stowagePlans,
        setStowagePlans,
        activeStowagePlan,
        isCopyStowagePlanMenuOpen,
        setIsCopyStowagePlanMenuOpen,
        isRenameStowagePlanMenuOpen,
        setIsRenameStowagePlanMenuOpen,
        setStowageModalData,
        pageHeaderContextMenuState,
        setPageHeaderContextMenuState,
    } = useStowageStore();
    const { role } = useAuthStore();
    const { setQueryParamStowagePlan, clearQueryParamsStowagePlan } = useStowageQueryParamsUtils();

    const hasRenamePermission = (role) => {
        return (
            activeStowagePlan &&
            ((activeStowagePlan.type === StowagePlanTypeEnum.Master && hasPermission(role, stowageFeatureEnum.Promote)) ||
                (activeStowagePlan.type === StowagePlanTypeEnum.Published && hasPermission(role, stowageFeatureEnum.Publish)) ||
                (activeStowagePlan.type === StowagePlanTypeEnum.Draft && hasPermission(role, stowageFeatureEnum.EditStowage)))
        );
    };

    const onRenameStowagePlan = async () => {
        setIsRenameStowagePlanMenuOpen(!isRenameStowagePlanMenuOpen);
        setIsStowagePlanListMenuOpen(false);
    };

    const onOpenCopyStowagePlanMenu = () => {
        setIsCopyStowagePlanMenuOpen(!isCopyStowagePlanMenuOpen);
        setIsStowagePlanListMenuOpen(false);
    };

    const onDeleteStowagePlan = () => {
        setStowageModalData({
            title: 'Delete stowage plan',
            message: 'Are you sure you want to delete this stowage plan?',
            action: () => deleteStowagePlan(),
            cancel: () => setStowageModalData(null),
            actionButtonText: 'Delete',
        });
    };

    const deleteStowagePlan = async () => {
        const _stowagePlan = { ...activeStowagePlan, isDeleted: true };
        const { updatedStowagePlan } = await updateStowagePlan(_stowagePlan, true);

        const filteredStowagePlans = [...stowagePlans.filter((s) => s.stowagePlanGuid !== activeStowagePlan.stowagePlanGuid)];
        const _stowagePlans = [...filteredStowagePlans, updatedStowagePlan];
        setStowagePlans(sortStowagePlans(_stowagePlans));

        const nonDeletedStowagePlans = filteredStowagePlans.filter((s) => !s.isDeleted);

        const masterStowagePlan = getMasterStowagePlan(_stowagePlans);
        const _stowagePlanGuid = masterStowagePlan
            ? masterStowagePlan.stowagePlanGuid
            : nonDeletedStowagePlans.length > 0
            ? nonDeletedStowagePlans[0].stowagePlanGuid
            : null;
        if (_stowagePlanGuid) {
            setQueryParamStowagePlan(_stowagePlanGuid);
        } else {
            clearQueryParamsStowagePlan();
        }
        setStowageModalData(null);
    };

    const closeContextMenu = () => {
        setPageHeaderContextMenuState({ visible: false, cargoContents: [] } as ContextMenuState);
    };

    const handleOperation = (event: React.MouseEvent, operation: () => void) => {
        event.stopPropagation();

        if (!activeStowagePlan) {
            closeContextMenu();
            return;
        }

        operation();
        closeContextMenu();
    };

    const menuItemConfigs = [
        {
            content: 'Rename stowage plan',
            permission: stowageFeatureEnum.EditStowage,
            isEnabled: () => hasRenamePermission(role),
            action: onRenameStowagePlan,
        },
        {
            content: 'Copy stowage plan',
            permission: stowageFeatureEnum.EditStowage,
            isEnabled: () => hasPermission(role, stowageFeatureEnum.EditStowage),
            action: onOpenCopyStowagePlanMenu,
        },
        {
            content: 'Delete stowage plan',
            permission: stowageFeatureEnum.EditStowage,
            isEnabled: () => activeStowagePlan && !activeStowagePlan.isNewStowagePlan && activeStowagePlan.type === StowagePlanTypeEnum.Draft,
            action: onDeleteStowagePlan,
        },
    ];

    const createMenuItems = (): MenuItem[] =>
        menuItemConfigs.map((config) => ({
            isSeparator: false,
            content: config.content,
            isDisabled: !(hasPermission(role, config.permission) && config.isEnabled?.()),
            onClick: config.action ? (event) => handleOperation(event, config.action) : undefined,
            className: undefined,
        }));

    const onOpenContextMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        setPageHeaderContextMenuState({
            visible: !pageHeaderContextMenuState.visible,
            tank: null,
            cargoContents: [],
            cargoOperationGuid: null,
            berthCall: null,
            x: 0,
            y: 0,
            violations: null,
        });
    };

    return (
        <MenuItemButton onClick={(e) => onOpenContextMenu(e)} title="Open menu">
            <FontAwesomeIcon icon="ellipsis-h" />
            {pageHeaderContextMenuState?.visible && (
                <OptionsMenuWrapper>
                    <OdfjellContextMenu
                        position={{ x: 0, y: 0 }}
                        menuItems={createMenuItems()}
                        dataContextMenu="page-header"
                        className="page-header-context-menu"
                    />
                </OptionsMenuWrapper>
            )}
        </MenuItemButton>
    );
};

export default PageHeaderContextMenu;
